import React, { useState, useEffect } from 'react';
import config from '../config.json';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';

// API URL 설정
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 홈 페이지 컴포넌트
function Home() {
  const { isAdmin } = useAuth();
  const [content, setContent] = useState({
    title: '',
    image: '',
    intro: '',
    news: [],
    announcements: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 데이터 가져오기
  useEffect(() => {
    fetchHomeContent();
  }, []);

  const fetchHomeContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/home`);
      
      // API 응답이 없거나 실패한 경우에도 계속 진행
      if (!response.ok) {
        throw new Error('API request failed');
      }
      
      const data = await response.json();

      if (data.success && data.data && data.data.length > 0) {
        // DB에서 데이터 로드 성공
        const title = data.data.find(item => item.section_type === 'title');
        const image = data.data.find(item => item.section_type === 'image');
        const intro = data.data.find(item => item.section_type === 'intro');
        const news = data.data.filter(item => item.section_type === 'news' && item.is_active);
        const announcements = data.data.filter(item => item.section_type === 'announcement' && item.is_active);

        setContent({
          title: title?.content || '',
          image: image?.content || `${config.images.serverUrl}${config.images.basePath}${config.images.homeLogo}`,
          intro: intro?.content || 'Welcome to RUBIS (Real-time Ubiquitous Systems) Laboratory at Seoul National University.',
          news: news,
          announcements: announcements
        });
      } else {
        // DB에 데이터가 없으면 기본 컨텐츠 사용
        setContent({
          title: '',
          image: `${config.images.serverUrl}${config.images.basePath}${config.images.homeLogo}`,
          intro: 'Welcome to RUBIS (Real-time Ubiquitous Systems) Laboratory at Seoul National University.',
          news: [],
          announcements: []
        });
      }
    } catch (err) {
      console.error('Error fetching home content:', err);
      // 에러 시 기본 컨텐츠 사용 (DB가 없어도 작동)
      setContent({
        title: '',
        image: `${config.images.serverUrl}${config.images.basePath}${config.images.homeLogo}`,
        intro: 'Welcome to RUBIS (Real-time Ubiquitous Systems) Laboratory at Seoul National University.',
        news: [],
        announcements: []
      });
      setError(null); // 에러 메시지 숨김
    } finally {
      setLoading(false);
    }
  };

  // 로딩 중
  if (loading) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="site-content">
      {/* Edit 버튼 - 관리자에게만 표시 */}
      {isAdmin && (
        <div style={{ textAlign: 'right', margin: '20px 0' }}>
          <Link to="/edit-home" style={{
            display: 'inline-block',
            padding: '8px 20px',
            backgroundColor: '#222',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: '3px',
            fontSize: '14px',
            border: 'none'
          }}>
            Edit Home Page
          </Link>
        </div>
      )}

      {/* 로고 이미지 */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <img 
          src={content.image}
          alt="RUBIS LAB" 
          title="RUBIS Laboratory"
          style={{ width: 'clamp(250px, 40vw, 400px)'/*'25%'*/, maxWidth: '400px', minWidth: '250px' }}
        />
      </div>

      {/* 제목 */}
      {content.title && content.title.trim() !== '' && (
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h2 style={{ 
            fontSize: '1rem', 
            fontWeight: 'normal',
            textDecoration: 'underline',
            margin: 0,
            fontFamily: '"Libre Franklin", "Helvetica Neue", helvetica, arial, sans-serif'
          }}>
            {content.title}
          </h2>
        </div>
      )}

      {/* Intro Section */}
      <section style={{ marginBottom: '50px' }}>
        <div style={{ 
          /*maxWidth: '50%'*/
	  width: 'clamp(360px, 50%, 960px)',
          margin: '0 auto',
          lineHeight: '1.8'
        }}>
          {content.intro.split('\n\n').map((paragraph, index) => (
            <p key={index} style={{ 
              fontSize: '15px', 
              margin: index === 0 ? '0 0 20px 0' : '20px 0',
	      textAlign: 'justify',
              /*textAlign: 'justify',*/
              lineHeight: '1.8',
              color: '#333'
            }}>
              &nbsp;&nbsp;&nbsp;&nbsp;{paragraph}
            </p>
          ))}
        </div>
      </section>

      {/* News Section */}
      {content.news.length > 0 && (
        <section style={{ marginBottom: '50px' }}>
          <h2 className="section-title">News</h2>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {content.news.map((item) => (
              <li key={item.ID} style={{ 
                marginBottom: '15px', 
                paddingBottom: '15px', 
                borderBottom: '1px solid #eee' 
              }}>
                {item.title && <strong>{item.title}</strong>}
                {item.content && (
                  <p style={{ margin: '5px 0', color: '#555' }}>{item.content}</p>
                )}
                {item.link && (
                  <a 
                    href={item.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#0066cc', textDecoration: 'none' }}
                  >
                    Read more →
                  </a>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Announcements Section */}
      {content.announcements.length > 0 && (
        <section style={{ marginBottom: '50px' }}>
          <h2 className="section-title">Announcements</h2>
          <ul style={{ listStyle: 'disc', paddingLeft: '40px', lineHeight: '2' }}>
            {content.announcements.map((item) => (
              <li key={item.ID}>
                {item.title && <strong>{item.title}</strong>}
                {item.content && <span> - {item.content}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default Home;
